import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { getQueue, clearQueue } from '../services/offlineService';

import { AppDispatch, RootState } from './configureStore';
import { ConnectivityData, Request } from '../utils/models';

import httpService from '../services/httpService';
import toastService from '../services/toastService';
import authService from '../services/authService';

const initialState: ConnectivityData = {
	isSynchronizing: false,
	isConnected: false,
	lastSynced: null,
};

const slice = createSlice({
	name: 'connectivity',
	initialState,
	reducers: {
		updateRequested: (connectivity) => {
			connectivity.isSynchronizing = true;
		},
		updateConnectionStatus: (connectivity) => {
			connectivity.isConnected = navigator.onLine;
		},
		updateSuccess: (connectivity) => {
			connectivity.isSynchronizing = false;
			connectivity.lastSynced = Date.now();
		},
		updateFailed: (connectivity) => {
			connectivity.isSynchronizing = false;
		},
	},
});

const refreshRequestAccessToken = (request: Request): Request => {
	const token = authService.getToken();
	if (!token) return request;

	return {
		...request,
		headers: { ...request.headers, 'x-access-token': token },
	};
};

const syncRequests = () => async (dispatch: AppDispatch) => {
	dispatch(updateRequested());

	try {
		const queue = getQueue();
		for (let queueItem of queue) {
			const request = JSON.parse(queueItem) as Request;
			await httpService.request(refreshRequestAccessToken(request));
		}

		clearQueue();
		dispatch(updateSuccess());
	} catch (error: any) {
		dispatch(updateFailed());

		throw new Error(error.message);
	}
};

export const {
	updateConnectionStatus,
	updateRequested,
	updateFailed,
	updateSuccess,
} = slice.actions;

export const performSynchronization = () => async (dispatch: AppDispatch) => {
	const queue = getQueue();
	if (queue.length === 0) return;

	toastService.promise(dispatch(syncRequests()), {
		loading: 'Synchronizing stored requests',
		success: () => 'Stored requests have been synchronized!',
		error: (ex: any) => ex.message,
	});
};

export const getConnectivity = createSelector(
	(state: RootState) => state,
	(state) => state.connectivity
);

export default slice.reducer;
